import { datadogLogs } from '@datadog/browser-logs';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { athleteDisplayName } from '@strength/domain';
import { MdClose } from '@teamworksdev/icons';
import { RouterInput, RouterOutput } from '../../../../../trpc';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../../trpc/react';
import { useToast } from '../../../../hooks/useToast';

type TransferData = RouterOutput['transfers']['fetchBlockTransferData'];
type AthleteTeamChange = TransferData['athleteTeamChanges'][0];
type Track = TransferData['tracksByTeam'][string][0];
type TransferBlockInput = RouterInput['transfers']['transferBlock'];
type TransferAction = TransferBlockInput['transferActions'][number];

type ActionType =
  | 'create-track'
  | 'create-subtrack'
  | 'add-to-existing'
  | 'no-action';

interface MoveAthleteBlockModalProps {
  open: boolean;
  onClose: () => void;
  blockId: string;
  teamId: string;
  onSave: (savedCount: number) => void;
}

export function MoveAthleteBlockModal({
  open,
  onClose,
  blockId,
  teamId,
  onSave,
}: MoveAthleteBlockModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { data: transferData } = trpc.transfers.fetchBlockTransferData.useQuery(
    { blockId },
    { enabled: open },
  );

  const transferBlock = trpc.transfers.transferBlock.useMutation();

  const [athleteData, setAthleteData] = useState<
    Record<string, TransferAction>
  >({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (transferData?.athleteTeamChanges) {
      const initial: Record<string, TransferAction> = {};
      transferData.athleteTeamChanges.forEach((athlete) => {
        initial[athlete.profileId] = {
          profileId: athlete.profileId,
          oldTeamId: athlete.oldTeamId,
          newTeamId: athlete.newTeamId,
          doNothing: true,
        };
      });
      setAthleteData(initial);
    }
  }, [transferData]);

  const isValid = useMemo(() => {
    if (saving) return false;
    return Object.values(athleteData).every(
      ({ doNothing, trackName, parentTrackId, subtrackId }) => {
        // "No action" action
        if (doNothing) return true;
        // Required for either create new track or subtrack
        if (trackName && trackName.trim().length > 0) return true;
        // If a subtrackId is specified, we must have a parent track, too
        if (subtrackId && parentTrackId) return true;
        if (parentTrackId) return true;

        return false;
      },
    );
  }, [saving, athleteData]);

  const handleUpdate = useCallback(
    (update: TransferAction) => {
      setAthleteData((prev) => ({
        ...prev,
        [update.profileId]: { ...update },
      }));
    },
    [setAthleteData],
  );

  const handleSave = () => {
    if (saving) return;
    setSaving(true);
    transferBlock.mutate(
      {
        blockId,
        transferActions: Object.values(athleteData),
      },
      {
        onSuccess: () => {
          setSaving(false);
          onSave(Object.keys(athleteData).length);
        },
        onError: (e) => {
          datadogLogs.logger.error(e.message);
          setSaving(false);
          toast({
            title: t('error'),
            description: t('failedToTransferBlock'),
            severity: 'error',
          });
        },
      },
    );
  };

  if (!transferData) {
    return null; // Loading state
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex items-center justify-between p-6">
        {t('moveAthleteBlock')}
        <IconButton onClick={onClose} aria-label={t('close')}>
          <MdClose size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography className="text-sm text-gray-600 mb-6">
          {t('athletesTransferredMessage')}
        </Typography>

        <div className="flex w-full flex-col space-y-2">
          {transferData.athleteTeamChanges.map((athlete) => {
            return (
              <AthleteTransferAction
                key={athlete.profileId}
                athlete={athlete}
                tracks={transferData.tracksByTeam[athlete.newTeamId] || []}
                onChange={handleUpdate}
              />
            );
          })}
        </div>
      </DialogContent>

      <DialogActions className="p-6">
        <Button variant="outlined" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface AthleteTransferActionProps {
  athlete: AthleteTeamChange;
  tracks: Track[];
  onChange: (change: TransferAction) => void;
}
function AthleteTransferAction({
  athlete,
  tracks,
  onChange,
}: AthleteTransferActionProps) {
  const { t } = useTranslation();
  const [action, setAction] = useState<ActionType>('no-action');
  const [trackName, setTrackName] = useState<string>('');
  const [parentTrackId, setParentTrackId] = useState<string>('');
  const [subtrackId, setSubtrackId] = useState<string>('no-subtrack');
  // Subtracks for selected parent track
  const [subtracks, setSubtracks] = useState<Track[]>([]);

  const parentTracks = useMemo(
    () => tracks.filter((t) => !t.parentTrackId),
    [tracks],
  );
  const subtracksByParentTrackId = useMemo(() => {
    return tracks.reduce(
      (acc, next) => {
        if (!next.parentTrackId) return acc;
        const subtracks = acc[next.parentTrackId] ?? [];
        subtracks.push(next);
        acc[next.parentTrackId] = subtracks;
        return acc;
      },
      {} as Record<string, Track[]>,
    );
  }, [tracks]);

  useEffect(() => {
    onChange({
      profileId: athlete.profileId,
      oldTeamId: athlete.oldTeamId,
      newTeamId: athlete.newTeamId,
      doNothing: action === 'no-action',
      parentTrackId:
        action === 'create-subtrack' || action === 'add-to-existing'
          ? parentTrackId
          : undefined,
      subtrackId:
        action === 'add-to-existing' && subtrackId !== 'no-subtrack'
          ? subtrackId
          : undefined,
      trackName:
        action === 'create-track' || action === 'create-subtrack'
          ? trackName
          : undefined,
    });
  }, [onChange, athlete, action, parentTrackId, subtrackId, trackName]);

  const resetValues = useCallback(() => {
    setTrackName('');
    setParentTrackId('');
    setSubtrackId('no-subtrack');
  }, [setTrackName, setParentTrackId, setSubtrackId]);

  return (
    <div className="flex space-x-4 items-end w-full bg-gray-100 p-4 rounded-md">
      <div className="flex-nowrap">
        <Typography component="p" className="font-semibold text-nowrap">
          {athleteDisplayName(athlete)}
        </Typography>
        <Typography className="text-sm text-gray-500 text-nowrap mr-4">
          {`${athlete.oldTeamName} → ${athlete.newTeamName}`}
        </Typography>
      </div>

      <FormControl className="min-w-52">
        <TextField
          label={t('action')}
          fullWidth
          select
          onChange={(e) => {
            setAction(e.target.value as ActionType);
            resetValues();
          }}
          value={action}
        >
          <MenuItem value="create-track">{t('createNewTrack')}</MenuItem>
          <MenuItem value="create-subtrack">{t('createNewSubtrack')}</MenuItem>
          <MenuItem value="add-to-existing">{t('addToExistingTrack')}</MenuItem>
          <MenuItem value="no-action">{t('noAction')}</MenuItem>
        </TextField>
      </FormControl>

      {(action === 'create-subtrack' || action === 'add-to-existing') && (
        <FormControl className="min-w-52">
          <TextField
            label={t('parentTrack')}
            select
            fullWidth
            onChange={(e) => {
              setParentTrackId(e.target.value);
              setSubtracks(subtracksByParentTrackId[e.target.value] ?? []);
              setSubtrackId('no-subtrack');
            }}
            value={parentTrackId}
          >
            {parentTracks.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      )}

      {action === 'add-to-existing' && subtracks.length > 0 && (
        <FormControl className="min-w-52">
          <TextField
            label={t('subtrack')}
            select
            fullWidth
            onChange={(e) => {
              setSubtrackId(e.target.value);
            }}
            value={subtrackId}
            className={subtrackId === 'no-subtrack' ? 'text-gray-600' : ''}
            inputProps={{
              className: subtrackId === 'no-subtrack' ? 'text-gray-600' : '',
            }}
          >
            {[null, ...subtracks].map((track) => (
              <MenuItem
                key={track?.id ?? ''}
                value={track?.id ?? 'no-subtrack'}
                className={track ? '' : 'text-gray-600'}
              >
                {track?.name ?? t('noSubtrack')}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      )}

      {(action === 'create-track' ||
        (action === 'create-subtrack' && !!parentTrackId)) && (
        <FormControl className="min-w-52">
          <TextField
            label={t('trackName')}
            fullWidth
            value={trackName}
            onChange={(e) => {
              setTrackName(e.target.value);
            }}
          />
        </FormControl>
      )}
    </div>
  );
}
